import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Clients: React.FC = () => {
  // Updated clients array with logo file names and display names
  const clients = [
    { name: 'EBIXCASH', logo: 'Ebixcash.png' },
    { name: 'ATHENA', logo: 'Athena.jpg' },
    { name: 'CALL TO CONNECT', logo: 'Call to connect.jpg' },
    { name: 'TAURUS', logo: 'Taurus.jpg' },
    { name: 'RELIABLE IT PRIVATE LIMITED', logo: 'Reliable it private limited.jpeg' },
    { name: 'PACE SETTERS PRIVATE LIMITED', logo: 'Pace setters private limited.jpg' },
    { name: 'SIS', logo: 'sis new.webp' },
    { name: 'CONCENTRIX', logo: 'Concentrix.png' },
    { name: 'DEV DAYANAND AYURVEDIC HOSPITAL', logo: 'Dev Dayanand ayurvedic hospital.png' },
    { name: 'KEVA KAIPO INDUSTRY PVT. LTD', logo: 'Keva Kaipo industry Pvt. Ltd.jpg' },
    { name: 'HAZELNUT CYBORG INFRA SOLUTIONS PVT. LTD', logo: 'Hazelnut Cyborg Infra Solutions Pvt. Ltd.png' },
    { name: 'SAJNA COUTURE', logo: 'Sajna Courture.jpeg' },
    { name: 'SHREE KARNI ROAD CARRIERS', logo: 'Shree Karni Road Carriers.jpg' },
    { name: 'SHREE KARNI LIFTERS AND MOVERS', logo: 'Shree Karni Lifter and Movers.jpeg' },
    { name: 'SAFFRON INFRATECH', logo: 'Saffron Infratech.png' }
  ];

  const testimonials = [
    {
      name: "Rajesh Sharma",
      position: "HR Director, Tech Solutions Inc.",
      content: "Skill Point Consultants has been instrumental in helping us build our tech team. Their understanding of our requirements and quick delivery of quality candidates has been exceptional.",
      company: "Tech Solutions Inc."
    },
    {
      name: "Priya Malhotra",
      position: "Recruitment Manager, Global Services Ltd.",
      content: "Working with Skill Point Consultants has transformed our hiring process. They consistently provide high-quality candidates who match not just our skill requirements but also our company culture.",
      company: "Global Services Ltd."
    },
    {
      name: "Amit Patel",
      position: "CEO, StartUp Innovations",
      content: "As a growing startup, finding the right talent was crucial for us. Skill Point Consultants understood our unique needs and helped us build a stellar team that has been key to our success.",
      company: "StartUp Innovations"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="section-title"
            >
              Our Clients
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="text-lg md:text-xl text-gray-700 mt-8"
            >
              We're proud to partner with leading organizations across various industries.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Clients Grid Section with Logos */}
      <section className="section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8"
          >
            {clients.map((client, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                whileHover={{ scale: 1.05, boxShadow: "0 10px 30px -15px rgba(0, 0, 0, 0.2)" }}
                className="bg-white rounded-lg shadow-custom p-4 flex flex-col items-center justify-center h-48 transition-all duration-300"
              >
                <div className="h-24 flex items-center justify-center mb-4 overflow-hidden flex-grow">
                  <img 
                    src={`/client-logos/${client.logo}`} 
                    alt={`${client.name} logo`}
                    className="max-h-full max-w-full object-contain"
                    style={{ display: 'block', margin: 'auto', maxWidth: '85%', maxHeight: '85%' }}
                  />
                </div>
                <h3 className="text-center font-bold text-gray-800 text-sm mt-auto uppercase tracking-tight">{client.name}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Counter Section */}
      <section className="section bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center p-6"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-5xl font-bold mb-3"
              >
                500+
              </motion.div>
              <h3 className="text-xl font-medium">Successful Placements</h3>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center p-6"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-5xl font-bold mb-3"
              >
                50+
              </motion.div>
              <h3 className="text-xl font-medium">Corporate Clients</h3>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center p-6"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-5xl font-bold mb-3"
              >
                10+
              </motion.div>
              <h3 className="text-xl font-medium">Industries Served</h3>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center p-6"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-5xl font-bold mb-3"
              >
                5+
              </motion.div>
              <h3 className="text-xl font-medium">Years of Excellence</h3>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            What Our Clients Say
          </motion.h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="card p-8 relative"
              >
                <div className="absolute top-0 right-0 transform translate-x-3 -translate-y-3 text-8xl text-primary opacity-10 font-serif">
                  "
                </div>
                <p className="text-gray-700 mb-6 relative z-10">"{testimonial.content}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-bold text-lg">{testimonial.name}</h3>
                  <p className="text-gray-600 text-sm">{testimonial.position}</p>
                  <p className="text-primary font-medium text-sm mt-1">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner With Us Section */}
      <section className="section py-20 bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold mb-6 text-white"
            >
              Become Our Next Success Story
            </motion.h2>
            
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-lg mb-10 text-white/90"
            >
              Join our growing list of satisfied clients and experience the difference of working with a dedicated staffing partner.
            </motion.p>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Link to="/contact" className="btn-secondary inline-flex items-center justify-center px-8 py-3 min-w-[160px] text-base font-medium rounded-md shadow-md hover:shadow-lg transition-all duration-300">
                Get in Touch
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Clients; 